import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DRAWER_WIDTH } from 'constants/ui';
import { OpFormDrawer } from 'components/customAntd/DLS/OpFormDrawer/OpFormDrawer';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { getRequest, patchRequest, postRequest } from 'api/apiClient';
import { VisitorType } from 'types/VisitorTypeTypes';
import { User } from 'types/userTypes';
import { formatFullName, hasPermission, transformNullToEmptyString } from 'utils/utils';
import VisitorList from '../formComponents/VisitorList';
import { notification } from 'antd';
import { VisitorInvite } from 'types/visitorInvitationTypes';
import STATUS from 'constants/status';
import { DATE_TIME_FORMAT } from 'constants/dates';
import dayjs from 'dayjs';
import { SIGNED_IN_VISITOR_ID } from 'constants/userActivities';
import { fetchVisits } from 'store/slices/visitsSlice';
import { FormFieldWorkflow } from 'types/formFieldTypes';
import { OpDivider } from 'components/customAntd/DLS/OpDivider/OpDivider';
import { VisitorWorkflowFields } from 'types/visitorWorkflowTypes';
import { searchVisitors, searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import { fetchLocations } from 'store/slices/locationsSlice';
import { Location, LocationWorkflow } from 'types/locationTypes';
import { useTranslation } from 'react-i18next';
import { ConfirmDeniedData } from 'types/denyTypes';
import { fetchRedFlags } from 'store/slices/redFlagSlice';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import SignInManuallyAccessControlModal from './footer/SignInManuallyAccessControlModal';
import { fetchCustomFields } from 'store/slices/customFieldsSlice';
import i18n from 'i18n/i18n';

interface EnterManuallyComponentProps {
    open: boolean;
    locationIdRef: number;
    onClose: () => void;
    newVisitorName?: {
        firstName: string;
        middleName: string;
        lastName: string;
        dob?: string;
    };
    setNewVisitorName: (filter: { firstName: string; middleName: string; lastName: string; dob?: string } | undefined) => void;
}

const SignInManuallyDrawer: React.FC<EnterManuallyComponentProps> = ({ open, locationIdRef, onClose, newVisitorName, setNewVisitorName }) => {
    const { t } = useTranslation();

    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const lastSearchParams = useSelector((state: RootState) => state.visitorSearch.lastSearchParams);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const { locations } = useSelector((state: RootState) => state.locations);

    const [loading, setLoading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [hosts, setHosts] = useState<User[]>([]);
    const [visitorTypes, setVisitorTypes] = useState<VisitorType[]>([]);
    const [locationVisitorTypes, setLocationVisitorTypes] = useState<VisitorType[]>([]);
    const [locationDefaultWorkflow, setLocationDefaultWorkflow] = useState<LocationWorkflow>();
    const [signInWorkflowFields, setSignInWorkflowFields] = useState<VisitorWorkflowFields>();
    const [signInWorkflowCustomFields, setSignInWorkflowCustomFields] = useState<FormFieldWorkflow[]>([]);
    const [isAccessControlModalOpen, setIsAccessControlModalOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<number>(0);

    const hasVisitorlogWrite = hasPermission(tokenScopeList, orgId, 'o', 'visitorlog:w');
    const hasAllVisitorsWrite = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:w');
    const hasDenyWrite = hasPermission(tokenScopeList, orgId, 'o', 'deny:w');

    const isInitialLoad = useRef(true);

    const [form] = OpForm.useForm();
    const selectedVisitorTypeId = OpForm.useWatch("visitorTypeId", form);

    const initialValues = {
        siteId: locationIdRef,
        visitorTypeId: visitorTypes[0]?.id,
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const locationWorkflowResponse = await getRequest(`/orgs/${orgId}/sites/${locationIdRef}/workflows`);
                const locationVisitorTypesReponse = await getRequest(`/orgs/${orgId}/sites/${locationIdRef}/visitorType`);
                const signInWorkflowFieldsResponse = await getRequest(`/orgs/${orgId}/visitorWorkflow/${locationWorkflowResponse.data[0].SignInWorkflowId}/fieldConfig`);

                setLocationDefaultWorkflow(locationWorkflowResponse.data[0]); // Default location workflow
                setLocationVisitorTypes(locationVisitorTypesReponse.data); // Visitor Types that contains workflows by visitor type
                setSignInWorkflowFields(signInWorkflowFieldsResponse.data[0]);

                await dispatch(fetchCustomFields({ orgId }));

                if (orgId && locations.length === 0) {
                    dispatch(fetchLocations({ orgId, status: 1 }));
                }

                const hosts = await getRequest(`/orgs/${orgId}/users`);
                const filteredHosts = hosts.data.filter((user: User) => user.status === 1 && (user.hostExclude === 0 || user.hostExclude === null));
                setHosts(filteredHosts);

                const visitorTypes = await getRequest(`/orgs/${orgId}/visitorTypes`);
                setVisitorTypes(visitorTypes.data);

                const signInWorkflowCustomFields = await getRequest(`/orgs/${orgId}/visitorWorkflow/${locationWorkflowResponse.data[0].SignInWorkflowId}/fieldCustom`);
                setSignInWorkflowCustomFields(signInWorkflowCustomFields.data);

            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [orgId, locationIdRef, dispatch, locations.length]);

    useEffect(() => {
        const handleVisitorTypeChange = async () => {
            // Return if datas are not ready
            if (!selectedVisitorTypeId || locationVisitorTypes.length === 0) return;

            // Return if it's initial load (fields are already set during initial data setting)
            if (isInitialLoad.current) {
                isInitialLoad.current = false;
                return;
            }

            const selectedVisitorType = locationVisitorTypes.find(v => v.id === selectedVisitorTypeId);

            const hasVisitorTypeSignInWorkflow = (locationVisitorTypes ?? []).some(
                (type) => type.workflow?.SignInWorkflowId && type.workflow.SignInWorkflowId !== -1
            );

            // Return if selected visitor type doesn't exist in location visitor types or any workflow didn't get allocated to any visitor type
            if (!selectedVisitorType || !hasVisitorTypeSignInWorkflow) return;

            const workflowId = selectedVisitorType.workflow?.SignInWorkflowId;
            const finalWorkflowId = workflowId !== -1
                ? workflowId
                : locationDefaultWorkflow?.SignInWorkflowId;

            if (!finalWorkflowId) return;

            try {
                const fieldConfig = await getRequest(`/orgs/${orgId}/visitorWorkflow/${finalWorkflowId}/fieldConfig`);
                setSignInWorkflowFields(fieldConfig.data[0]);

                const fieldCustom = await getRequest(`/orgs/${orgId}/visitorWorkflow/${finalWorkflowId}/fieldCustom`);
                setSignInWorkflowCustomFields(fieldCustom.data);

                if (hasVisitorTypeSignInWorkflow) {
                    notification.info({
                        message: t('notification.notification'),
                        description: t('signInManuallyDrawer.notification.changeFieldsNotification'),
                        placement: 'bottomRight',
                    });
                }

            } catch (error) {
                notification.error({
                    message: t('notification.error'),
                    description: t('signInManuallyDrawer.notification.changeFieldsError'),
                    placement: 'bottomRight',
                });
            }
        };
        handleVisitorTypeChange();

    }, [orgId, selectedVisitorTypeId, locationDefaultWorkflow, locationVisitorTypes, t]);

    const renderFormFields = () => {
        return signInWorkflowCustomFields.map((formField, index) => {
            const capitalizedLabel = formField.name;
            if (formField.values) {
                const options = formField.values.map((option) => ({
                    label: option.display || option.name,
                    value: option.value,
                }));

                return (
                    <OpForm.Select
                        label={capitalizedLabel}
                        name={formField.id}
                        options={options}
                        key={formField.id || index.toString()}
                        rules={[{ required: formField.required === 1 }]}
                        placeholder={formField.placeholder ? formField.placeholder : undefined}
                        allowClear
                    />
                );
            } else {
                return (
                    <OpForm.TextAreaInput
                        label={capitalizedLabel}
                        name={formField.id}
                        key={formField.id || index.toString()}
                        rows={4}
                        rules={[{ required: formField.required === 1 }]}
                        placeholder={formField.placeholder ? formField.placeholder : undefined}
                    />
                );
            }
        });
    };

    const handleConfirmedDenied = ({ selectedVisitors, reasonId, details, addToBlockList, addToWatchList }: ConfirmDeniedData) => {
        const visitors = form.getFieldValue('visitors') || [];
        const updatedVisitors = visitors.map((visitor: any) => {
            const isSelected = selectedVisitors.includes(String(visitor.id));

            if (isSelected) {
                return {
                    ...visitor,
                    status: STATUS.DENIED_ENTRY.id,
                    reasonId,
                    details,
                    addToBlockList,
                    addToWatchList,
                };
            } else if (visitor.status === STATUS.DENIED_ENTRY.id) {
                const { status, reasonId, details, addToBlockList, addToWatchList, ...rest } = visitor;
                return rest;
            }

            return visitor;
        });

        form.setFieldsValue({ visitors: updatedVisitors });
    };

    const validateVisitorModalFields = useCallback((visitors: any[], signInWorkflowFields: any) => {
        if (!signInWorkflowFields) return visitors;

        const middleNameRequired = signInWorkflowFields.middleName?.included === 1 && signInWorkflowFields.middleName?.required === 1;
        const emailRequired = signInWorkflowFields.email?.included === 1 && signInWorkflowFields.email?.required === 1;
        const phoneNameRequired = signInWorkflowFields.visitPhone?.included === 1 && signInWorkflowFields.visitPhone?.required === 1;

        return visitors.map((v: any) => {
            const missingFields =
                (middleNameRequired && !v.middleName?.trim()) ||
                (emailRequired && !v.email?.trim()) ||
                (phoneNameRequired && !v.mobilePhone?.trim());

            return {
                ...v,
                error: missingFields ? t('notification.validationError') : undefined
            };
        });
    }, [t]);

    // Validate visitor modal fields when visitor type changes
    useEffect(() => {
        const visitors = form.getFieldValue("visitors") || [];
        form.setFieldsValue({
            visitors: validateVisitorModalFields(visitors, signInWorkflowFields)
        });
    }, [form, selectedVisitorTypeId, signInWorkflowFields, validateVisitorModalFields]);

    const handleSubmit = useCallback(async ({ values }: IOnSubmitArgs) => {
        setIsSubmitting(true);
        try {
            if (values.visitors === undefined || values.visitors.length === 0) {
                notification.error({
                    message: t('notification.error'),
                    description: t('signInManuallyDrawer.notification.signInError'),
                    placement: 'bottomRight',
                });
                setIsSubmitting(false);
                return;
            }

            const validated = validateVisitorModalFields(values.visitors, signInWorkflowFields);
            if (validated.some(v => v.error)) {
                form.setFieldsValue({ visitors: validated });
                notification.error({
                    message: t('notification.error'),
                    description: t('notification.validationError'),
                    placement: 'bottomRight',
                });
                setIsSubmitting(false);
                return;
            }

            const transformedValues = transformNullToEmptyString(values);

            // Filter out numeric keys directly
            let filteredValues = Object.keys(transformedValues).reduce((acc, key) => {

                // Exclude Custom Fields
                if (isNaN(Number(key))) {
                    (acc as Record<string, any>)[key] = transformedValues[key];
                }

                // Exclude middleName, email, or mobilePhone from payload if scope is turned off
                if (key === "visitors" && Array.isArray(transformedValues.visitors)) {
                    acc.visitors = transformedValues.visitors.map((visitor: any) => {
                        const visitorPayload = { ...visitor };

                        if (signInWorkflowFields?.middleName?.included === 0) delete visitorPayload.middleName;
                        if (signInWorkflowFields?.email?.included === 0) delete visitorPayload.email;
                        if (signInWorkflowFields?.visitPhone?.included === 0) delete visitorPayload.mobilePhone;

                        return visitorPayload;
                    });
                }
                return acc;
            }, {} as Record<string, any>);

            // Immediately sign in visitors upon sending an invitation.
            const inviteResponse = await postRequest(`/orgs/${orgId}/visitorInvitation?language=${i18n.language.split('-')[0]}`, {
                ...filteredValues,
                sendHostEmail: 0,
                visitors: filteredValues.visitors.map((visitor: any) => ({
                    // ...visitor,
                    firstName: visitor.firstName?.trim(),
                    middleName: String(visitor.id), // Will be replaced with real middle name from filteredValues
                    lastName: visitor.lastName?.trim(),
                    email: visitor.email?.trim(),
                    mobilePhone: visitor.mobilePhone?.trim(),
                    status: STATUS.PENDING.id,
                })),
            });

            const visit = inviteResponse.data[0];
            let hasDeniedVisitor = false;

            for (const visitor of visit.visitors) {
                if (visitor.id) {

                    // Match the temporary id with middleName (contains temporary id)
                    const matched = filteredValues.visitors.find((localVisitor: any) => {
                        return String(localVisitor.id) === visitor.middleName
                    });

                    if (matched?.status === STATUS.DENIED_ENTRY.id) { // Deny Entry
                        hasDeniedVisitor = true;
                        await patchRequest(`/orgs/${orgId}/visitor/${visit.id}/visitors/${visitor.id}`, {
                            signIn: dayjs().format(DATE_TIME_FORMAT),
                            middleName: matched.middleName ?? '', // Replace temporary id with real middle name
                            status: STATUS.DENIED_ENTRY.id
                        });
                        await postRequest(`/orgs/${orgId}/visitor/${visit.id}/denyEntry`, {
                            visitorId: Number(visitor.id),
                            deniedBy: Number(globalUserId),
                            reasonId: Number(matched.reasonId),
                            details: matched.details,
                        });

                        if (matched.addToBlockList || matched.addToWatchList) {
                            await postRequest(`/orgs/${orgId}/redflag`, {
                                level: matched.addToBlockList ? "1" : "2",
                                effectiveDate: dayjs().format(DATE_TIME_FORMAT),
                                visitors: [
                                    {
                                        firstName: matched.firstName,
                                        lastName: matched.lastName,
                                        middleName: matched.middleName,
                                    }
                                ]
                            })
                        };

                    } else { // Sign In
                        await patchRequest(`/orgs/${orgId}/visitor/${visit.id}/visitors/${visitor.id}`, {
                            signIn: dayjs().format(DATE_TIME_FORMAT),
                            middleName: matched.middleName ?? '', // Replace temporary id with real middle name
                            status: STATUS.SIGNED_IN.id
                        });
                    }
                }
            }

            // If hasDeniedVisitor exists, update visit status to Denied Entry. Otherwise, update visit status to Signed In
            await patchRequest(`/orgs/${orgId}/visitor/${visit.id}?language=${i18n.language.split('-')[0]}`, {
                signIn: dayjs().format(DATE_TIME_FORMAT),
                status: hasDeniedVisitor ? STATUS.DENIED_ENTRY.id : STATUS.SIGNED_IN.id
            });

            //Fetch RedFlags so that the table can be updated
            dispatch(fetchRedFlags({ orgId }));

            for (const key in values) {
                const field = values[key];
                // Check if the field is a custom field (custom fields have numeric keys)
                const isCustomField = !isNaN(Number(key));
                if (isCustomField && field !== undefined) {
                    try {
                        await postRequest(`/orgs/${orgId}/visitor/${visit.id}/visitorData`, {
                            fieldId: key,
                            value: field,
                        });
                        console.log(`Added custom field ${key}.`);
                    } catch (error) {
                        console.log(`Failed to add custom field ${key}:`, error);
                    }
                }
            }

            // log activity
            const fullNameList = values.visitors.map((visitor: VisitorInvite) =>
                formatFullName(visitor.firstName, visitor.middleName, visitor.lastName,)).join(', ');
            await postRequest(`/orgs/${orgId}/userActivity`, {
                userId: globalUserId,
                activityId: SIGNED_IN_VISITOR_ID,
                details: fullNameList
            });

            notification.success({
                message: t('notification.success'),
                description: t('signInManuallyDrawer.notification.signInSuccess'),
                placement: 'bottomRight',
            });

            await dispatch(fetchVisits({ orgId }));
            await dispatch(searchVisitorsToday({ orgId }));
            if (Object.keys(lastSearchParams).length !== 0) {
                await dispatch(searchVisitors({
                    ...lastSearchParams,
                    orgId,
                    siteId: lastSearchParams.siteId || globalLocationId!
                }));
            }
            form.resetFields();
            onClose();
        } catch (error) {
            notification.error({
                message: t('notification.error'),
                description: t('signInManuallyDrawer.notification.submissionError'),
                placement: 'bottomRight',
            });
            console.error("Form submission failed:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [orgId, form, globalUserId, t, globalLocationId, lastSearchParams, signInWorkflowFields, dispatch, onClose, validateVisitorModalFields]);

    const filteredHosts = hasAllVisitorsWrite
        ? hosts
        : hosts.filter(host => host.id === globalUserId);


    return (
        <>
            <OpFormDrawer
                form={form}
                title={t('signInManuallyDrawer.title')}
                width={DRAWER_WIDTH}
                isFormLoading={loading || isSubmitting}
                open={open}
                onClose={() => {
                    onClose();
                    setNewVisitorName(undefined);
                }}
                leftFooter={hasDenyWrite && (
                    <OpButton
                        type="primary" danger ghost
                        style={{ width: "100%" }}
                        disabled={loading || isSubmitting}
                        onClick={() => {
                            if (form.getFieldValue('visitors') === undefined || form.getFieldValue('visitors').length === 0) {
                                notification.error({
                                    message: t('notification.error'),
                                    description: t('signInManuallyDrawer.notification.signInError'),
                                    placement: 'bottomRight',
                                });
                                return;
                            }
                            setIsAccessControlModalOpen(true);
                            setNewStatus(Number(STATUS.DENIED_ENTRY.id));
                        }}
                    >
                        {t('signInManuallyDrawer.footer.denyEntry')}
                    </OpButton>
                )}
                formComponent={
                    <OpForm
                        form={form}
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        hasError={false}
                        defaultButtons={false}
                    >
                        <VisitorList
                            form={form}
                            signInWorkflowFields={signInWorkflowFields}
                            isInviting={false}
                            newVisitorName={newVisitorName}
                            setNewVisitorName={setNewVisitorName}
                            selectedVisitorTypeId={selectedVisitorTypeId}
                            onVisitorTypeChange={(value) => form.setFieldsValue({ visitorTypeId: value })}
                            locationVisitorTypes={locationVisitorTypes}
                        />

                        <OpForm.Select
                            label={t('signInManuallyDrawer.visitorType.label')}
                            name="visitorTypeId"
                            rules={[{ required: true, message: t('signInManuallyDrawer.visitorType.required') }]}
                            placeholder={t('signInManuallyDrawer.visitorType.placeholder')}
                            options={visitorTypes.map(type => ({
                                label: t(type.languageCode ?? ''),
                                value: type.id
                            }))}
                        />

                        <OpForm.Select
                            label={t('signInManuallyDrawer.location.label')}
                            name="siteId"
                            rules={[{ required: true, message: t('signInManuallyDrawer.location.required') }]}
                            placeholder={t('signInManuallyDrawer.location.placeholder')}
                            options={locations.map((location: Location) => ({
                                label: location.name,
                                value: location.id
                            }))}
                            disabled={true}
                        />

                        {(signInWorkflowFields?.visitCompany.included === 1) && (
                            <OpForm.Input
                                label={t('signInManuallyDrawer.company.label')}
                                name="company"
                                rules={[{ required: signInWorkflowFields?.visitCompany.required === 1, message: t('signInManuallyDrawer.company.required') }]}
                            />
                        )}

                        {(signInWorkflowFields?.host.included === 1) && (
                            <OpForm.Select
                                label={t('signInManuallyDrawer.host.label')}
                                name="hostId"
                                rules={[{
                                    required: signInWorkflowFields?.host.required === 1 && hosts.length > 0,
                                    message: t('signInManuallyDrawer.host.required')
                                }]}
                                filterOption={(input, option) => (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())}
                                showSearch
                                placeholder={filteredHosts.length === 0
                                    ? t('signInManuallyDrawer.host.placeholderNotAvailable')
                                    : t('signInManuallyDrawer.host.placeholder')}
                                disabled={filteredHosts.length === 0}
                                options={filteredHosts.map(host => ({
                                    label: host.identity?.firstName || host.identity?.lastName
                                        ? formatFullName(host.identity.firstName, host.identity.middleName, host.identity.lastName)
                                        : host.identity?.email,
                                    value: host.id
                                }))}
                                allowClear
                            />
                        )}

                        {(signInWorkflowFields?.visitPurpose.included === 1) && (
                            <OpForm.TextAreaInput
                                label={t('signInManuallyDrawer.purpose.label')}
                                name="purpose"
                                rows={4}
                                rules={[{ required: signInWorkflowFields?.visitPurpose.required === 1, message: t('signInManuallyDrawer.purpose.required') }]}
                            />
                        )}

                        {(signInWorkflowFields?.visitNotes?.included === 1 && hasVisitorlogWrite) && (
                            <OpForm.TextAreaInput
                                label={t('signInManuallyDrawer.note.label')}
                                name="note"
                                rows={4}
                                rules={[{ required: signInWorkflowFields.visitNotes.required === 1, message: t('signInManuallyDrawer.note.required') }]}
                            />
                        )}

                        <OpDivider />
                        {renderFormFields()}
                    </OpForm>
                }
            />
            <SignInManuallyAccessControlModal
                open={isAccessControlModalOpen}
                newStatus={newStatus}
                visitorList={form.getFieldValue('visitors') || []}
                onClose={() => setIsAccessControlModalOpen(false)}
                onConfirm={handleConfirmedDenied}
            />
        </>
    );
};

export default SignInManuallyDrawer;